import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, Play, Pause, RotateCcw, Loader2, Route } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const L = typeof window !== 'undefined' ? window.L : null;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
const SPEEDS = [1, 2, 5, 10];

const TripPlaybackModal = ({ load, onClose }) => {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const truck = useRef(null);
  const drawn = useRef(null);
  const timer = useRef(null);
  const [trail, setTrail] = useState(null);
  const [err, setErr] = useState('');
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(2);

  useEffect(() => {
    if (!load) return;
    axios.get(`${API}/tracking/loads/${load.id}/trail`, { withCredentials: true })
      .then((r) => setTrail(r.data)).catch((e) => setErr(e?.response?.data?.detail || 'Could not load trail'));
  }, [load]);

  useEffect(() => {
    if (!L || !mapRef.current || mapObj.current || !trail) return;
    const m = L.map(mapRef.current).setView([39.5, -98.35], 4);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap', maxZoom: 19 }).addTo(m);
    mapObj.current = m;
    const b = [];
    const pts = trail.points.map((p) => [p.lat, p.lng]);
    if (pts.length) { L.polyline(pts, { color: '#94a3b8', weight: 3, opacity: 0.6 }).addTo(m); b.push(...pts); }
    drawn.current = L.polyline([], { color: '#002FA7', weight: 4 }).addTo(m);
    if (trail.pickup?.coord) { L.circleMarker(trail.pickup.coord, { radius: 8, color: '#002FA7', fillColor: '#fff', fillOpacity: 1, weight: 3 }).addTo(m).bindPopup(`Pickup: ${trail.pickup.label || ''}`); b.push(trail.pickup.coord); }
    if (trail.delivery?.coord) { L.circleMarker(trail.delivery.coord, { radius: 8, color: '#16a34a', fillColor: '#16a34a', fillOpacity: 0.9 }).addTo(m).bindPopup(`Delivery: ${trail.delivery.label || ''}`); b.push(trail.delivery.coord); }
    (trail.auto_status_history || []).forEach((h) => {
      if (h.lat != null) L.circleMarker([h.lat, h.lng], { radius: 6, color: '#f59e0b', fillColor: '#f59e0b', fillOpacity: 1 }).addTo(m).bindPopup(`Auto: ${h.from} → ${h.to}<br/>${fmt(h.at)}`);
    });
    if (pts.length) {
      const icon = L.divIcon({ className: '', html: '<div style="background:#002FA7;color:#fff;border:2px solid #fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:13px;box-shadow:0 1px 4px rgba(0,0,0,.4)">🚚</div>', iconSize: [28, 28], iconAnchor: [14, 14] });
      truck.current = L.marker(pts[0], { icon }).addTo(m);
    }
    if (b.length) { try { m.fitBounds(b, { padding: [30, 30] }); } catch {} }
  }, [trail]);

  useEffect(() => {
    if (!trail || !truck.current) return;
    const pts = trail.points;
    if (!pts.length) return;
    const i = Math.min(idx, pts.length - 1);
    truck.current.setLatLng([pts[i].lat, pts[i].lng]);
    drawn.current?.setLatLngs(pts.slice(0, i + 1).map((p) => [p.lat, p.lng]));
  }, [idx, trail]);

  useEffect(() => {
    if (!playing || !trail) return undefined;
    timer.current = setInterval(() => setIdx((i) => Math.min(i + 1, trail.points.length - 1)), 600 / speed);
    return () => clearInterval(timer.current);
  }, [playing, speed, trail]);

  useEffect(() => {
    if (trail && playing && idx >= trail.points.length - 1) setPlaying(false);
  }, [idx, trail, playing]);

  if (!load) return null;
  const n = trail?.points?.length || 0;
  const cur = trail?.points?.[Math.min(idx, n - 1)];
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="trip-playback-modal" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-3xl shadow-xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 shrink-0">
          <div>
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}><Route className="w-4 h-4 text-[#002FA7]" /> Trip Playback · {load.load_ref || load.id.slice(-6)}</h3>
            <p className="text-xs text-slate-500">{load.pickup_location} → {load.delivery_location}{trail?.driver_name ? ` · ${trail.driver_name}` : ''}</p>
          </div>
          <button data-testid="trip-playback-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        {err ? <div className="p-6 text-sm text-red-600">{err}</div> : !trail ? (
          <div className="p-6 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading trail…</div>
        ) : n === 0 ? (
          <div className="p-6 text-sm text-slate-500" data-testid="trip-playback-empty">No GPS points were recorded for this load. Playback is available for loads tracked from the Live Tracking screen.</div>
        ) : (
          <>
            <div ref={mapRef} style={{ height: '52vh' }} data-testid="trip-playback-map" />
            <div className="px-5 py-3 border-t border-slate-100 space-y-2">
              <input type="range" min={0} max={n - 1} value={Math.min(idx, n - 1)} onChange={(e) => { setPlaying(false); setIdx(Number(e.target.value)); }}
                     className="w-full accent-[#002FA7]" data-testid="trip-playback-scrubber" />
              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
                <button data-testid="trip-playback-toggle" onClick={() => { if (idx >= n - 1) setIdx(0); setPlaying((p) => !p); }}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#002FA7] text-white font-semibold">
                  {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />} {playing ? 'Pause' : 'Play'}
                </button>
                <button data-testid="trip-playback-reset" onClick={() => { setPlaying(false); setIdx(0); }} className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-slate-200"><RotateCcw className="w-3.5 h-3.5" /> Reset</button>
                <div className="flex items-center gap-1">Speed
                  {SPEEDS.map((s) => <button key={s} onClick={() => setSpeed(s)} data-testid={`trip-playback-speed-${s}`} className={`px-2 py-1 rounded ${speed === s ? 'bg-slate-900 text-white' : 'bg-slate-100'}`}>{s}×</button>)}
                </div>
                <span className="ml-auto tabular-nums" data-testid="trip-playback-time">{cur ? fmt(cur.t) : ''} · point {Math.min(idx, n - 1) + 1}/{n}</span>
              </div>
              <div className="text-xs text-slate-500" data-testid="trip-playback-summary">
                {trail.summary.miles} mi tracked · {n} points · {fmt(trail.summary.started_at)} → {fmt(trail.summary.ended_at)}{trail.summary.delivered_at ? ` · delivered ${fmt(trail.summary.delivered_at)}` : ''}
                {(trail.auto_status_history || []).length > 0 && <span className="ml-2 text-amber-700">● {trail.auto_status_history.length} geofence auto-status event{trail.auto_status_history.length !== 1 ? 's' : ''}</span>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TripPlaybackModal;
